import { Link, createFileRoute } from "@tanstack/react-router";
import { MarketingPage, Section } from "@/components/marketing-page";
import { marketingHead, type FAQ } from "@/lib/seo";

const PATH = "/buyer-journey-intelligence";
const TITLE =
  "AI Buyer Journey Matrix, definition, framework, and methodology";
const DESCRIPTION =
  "Monroya is the only platform that segments LLM visibility by Discovery, Evaluation, and Decision stages across ChatGPT, Claude, Gemini, Perplexity, Google AI Overviews and Google AI Mode, segmented by persona.";
const TERM_DESC =
  "Measuring how a brand appears across the full sequence of buyer questions, from awareness through consideration to decision, inside generative AI answers, segmented by persona and stage.";

const FAQS: FAQ[] = [
  {
    q: "How is this different from AI visibility tracking?",
    a: "AI visibility tracking measures whether you appear. Buyer journey intelligence asks where in the journey you appear. You can be strong at the top (awareness) and invisible at the bottom (vendor selection), or the reverse, and the fix is different.",
  },
  {
    q: "What counts as a journey stage?",
    a: "We use three: awareness (problem framing), consideration (options and approaches), and decision (vendor selection and comparison). Some categories warrant a fourth stage for post-purchase or expansion prompts.",
  },
  {
    q: "Do I need to map my own buyer journey?",
    a: "We generate a draft prompt set from your category and ICP, organized by stage and persona. You review and edit. Most teams keep about 80% of the auto-generated prompts and add 10-20 that reflect their specific positioning.",
  },
  {
    q: "How granular do personas get?",
    a: "Role and stage by default, for example, 'VP Engineering, decision' versus 'Procurement, decision'. You can add more dimensions (industry, company size) if the buying motion meaningfully changes across them.",
  },
  {
    q: "What does the matrix actually show?",
    a: "A grid: stages on one axis, personas on the other. Each cell shows your share of voice for that slice and the top competitor surfacing alongside or instead of you. Click a cell to see the prompts driving it.",
  },
  {
    q: "How long does GEO take to show results?",
    a: "Technical indexing changes often reflect in AI responses within 3-7 days after schema deployment. Early visibility indicators, meaning movement in mention rate and citation rate on discovery and evaluation prompts, typically appear at the 2-6 week mark. Decision-stage share of voice is slower, usually 6-12 weeks, because it depends on third-party sources being recrawled.",
  },
];

export const Route = createFileRoute("/buyer-journey-intelligence")({
  head: () =>
    marketingHead({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Buyer journey intelligence", path: PATH },
      ],
      faqs: FAQS,
      definedTerm: {
        name: "Buyer journey intelligence",
        description: TERM_DESC,
      },
    }),
  component: BuyerJourneyPage,
});

function BuyerJourneyPage() {
  return (
    <MarketingPage
      stamp="Framework · Definition"
      title="The AI Buyer Journey Matrix."
      dek="Monroya is the only platform that segments LLM visibility by Discovery, Evaluation, and Decision stages, so you see exactly where buyers drop you before they ever reach your site."
      updated="October 2026"
      faqs={FAQS}
      related={[
        {
          label: "AI visibility tracking, the foundational definition",
          to: "/ai-visibility-tracking",
        },
        {
          label: "How Monroya runs the matrix",
          to: "/how-it-works",
        },
        {
          label: "For B2B SaaS marketing leaders",
          to: "/for/b2b",
          description: "Why Series A/B teams adopt this first",
        },
      ]}
    >
      <Section heading="The only stage-segmented LLM visibility platform">
        <p>
          Most AI visibility tools report one blended score. Monroya
          separates LLM visibility into the three stages buyers actually
          move through, <em>Discovery</em>, <em>Evaluation</em>, and{" "}
          <em>Decision</em> - so you can tell whether you have a
          discoverability problem, a comparison problem, or a
          vendor-selection problem. The fix is different for each, and
          only a stage-by-stage view shows which one is costing you
          pipeline.
        </p>
        <p>
          A fourth category, <em>Retention / churn risk</em>, sits outside
          the funnel and is tracked for every account: what AI tells your{" "}
          <em>existing</em> customers when they search for alternatives,
          complaints, or a way to cancel.
        </p>
      </Section>


      <Section heading="What our scan data shows">
        <p>
          Three numbers from Monroya's aggregated scans across ChatGPT,
          Claude, Gemini, and Perplexity explain why stage segmentation
          matters:
        </p>
        <ul className="mt-2 space-y-2 pl-5 [&amp;>li]:list-disc">
          <li>
            <strong>Average discovery-stage inclusion is 7.6%</strong>,
            versus <strong>37.7% at decision stage</strong> - brands are
            roughly 5× less visible when buyers are still framing the
            problem than when they're comparing named vendors.
          </li>
          <li>
            <strong>Evaluation-stage inclusion averages 23.3%</strong>,
            sitting much closer to discovery than decision, the middle
            of the funnel is where most brands quietly lose the buyer.
          </li>
          <li>
            <strong>
              78.9% of the brands we track have never been cited
            </strong>{" "}
            in a tracked answer: they get mentioned, but no source link points
            back to their site. Citation gaps are far more common than mention
            gaps.
          </li>
        </ul>
        <p className="mt-3 text-sm text-muted-foreground">
          Aggregated across Monroya customer scans, ChatGPT / Claude / Gemini /
          Perplexity, 90-day rolling window. Refreshed with each scan cycle.
        </p>
      </Section>

      <Section heading="Definition">
        <ul className="space-y-3 list-disc pl-5">
          <li>
            <strong>AI Buyer Journey Intelligence</strong> is the
            measurement of how a brand appears across the full sequence of
            questions a buyer asks a generative AI assistant, from first
            problem framing through final vendor selection, segmented by
            funnel stage and persona.
          </li>
          <li>
            <strong>The AI Buyer Journey Matrix</strong> is the canonical
            instrument: journey stages on one axis, buyer personas on the
            other.
          </li>
          <li>
            <strong>Each cell reports three things:</strong> its own AI
            Share of Voice, its top competing brand, and the specific
            prompts driving the result across ChatGPT, Claude, Gemini, and
            Perplexity.
          </li>
          <li>
            <strong>Why one blended score misleads:</strong> a 42% average
            can mean dominance in discovery and invisibility in decision (a
            deal-loss pattern), or the reverse (a brand-strength pattern
            with a discoverability gap). The fix differs for each, and only
            a stage-by-persona view shows which one you have.
          </li>
        </ul>
      </Section>

      <Section heading="The three stages, formally">
        <ul className="space-y-4 list-disc pl-5">
          <li>
            <strong>Discovery.</strong>
            <ul className="mt-1 space-y-1 list-disc pl-5">
              <li>Prompt type: problem framing, before the buyer knows a category exists.</li>
              <li>Example: <em>"why are our AI answers inconsistent across providers"</em>.</li>
              <li>Winning move: category-defining content on third-party sources AI assistants pull from.</li>
            </ul>
          </li>
          <li>
            <strong>Evaluation.</strong>
            <ul className="mt-1 space-y-1 list-disc pl-5">
              <li>Prompt type: options and approaches, once the category is understood.</li>
              <li>Example: <em>"best tools to monitor brand visibility in ChatGPT"</em>.</li>
              <li>Winning move: comparison and alternative pages, plus structured feature breakdowns on your own domain.</li>
            </ul>
          </li>
          <li>
            <strong>Decision.</strong>
            <ul className="mt-1 space-y-1 list-disc pl-5">
              <li>Prompt type: vendor selection and head-to-head.</li>
              <li>Example: <em>"monroya vs otterly pricing"</em>.</li>
              <li>Winning move: fact sheets, reviewer evidence, current pricing, direct competitor pages.</li>
            </ul>
          </li>
          <li>
            <strong>Retention / churn risk.</strong>
            <ul className="mt-1 space-y-1 list-disc pl-5">
              <li>Tracked separately because the asker is already your customer.</li>
              <li>Examples: <em>"better alternative to monroya"</em>, <em>"why are people leaving monroya"</em>, <em>"how do I cancel monroya"</em>.</li>
              <li>Winning move: make sure AI does not hand your renewal to a competitor. Every account gets these prompts tracked automatically.</li>
            </ul>
          </li>
        </ul>
      </Section>

      <Section heading="Why it matters: outside evidence">
        <ul className="space-y-3 list-disc pl-5">
          <li>
            G2's <em>2026 AI Search Insight Report</em> describes AI search
            as a third compression of the B2B software buying journey,
            where buyers act on a synthesized answer rather than a ranked
            list of vendors.{" "}
            <a
              className="underline underline-offset-2"
              href="https://learn.g2.com/g2-2026-ai-search-insight-report"
              target="_blank"
              rel="noopener noreferrer nofollow"
            >
              G2, The Answer Economy (2026)
            </a>
          </li>
          <li>
            An independent 2026 audit of 3,200 queries across ChatGPT,
            Perplexity, and Google AI Overviews puts AI citation share at
            roughly 17% of branded discovery for B2B SaaS, up from about 4%
            a year earlier, and finds cited brands are not simply the
            highest-ranked ones.{" "}
            <a
              className="underline underline-offset-2"
              href="https://winwithseo.com/insights/state-of-ai-search-2026"
              target="_blank"
              rel="noopener noreferrer nofollow"
            >
              The 2026 State of AI Search
            </a>
          </li>
          <li>
            Exposure Ninja's 2026 B2B and B2C buyer-journey report tracks AI
            assistant usage across separate stages of the purchase journey
            rather than as a single funnel-wide behaviour.{" "}
            <a
              className="underline underline-offset-2"
              href="https://exposureninja.com/blog/ai-search-buyer-journey/"
              target="_blank"
              rel="noopener noreferrer nofollow"
            >
              AI Search Buyer Journey, 2026
            </a>
          </li>
        </ul>
        <p className="mt-3 text-sm text-muted-foreground">
          External sources are cited for market context only. Every stage
          figure on this page comes from Monroya's own scan data.
        </p>
      </Section>



      <Section heading="GEO implementation timeline">
        <p>
          Generative engine optimization does not move on an SEO clock.
          Once you ship a fix, here is the sequence we observe across
          Monroya customer scans:
        </p>
        <ul className="mt-2 space-y-3 list-disc pl-5">
          <li>
            <strong>Days 0-3: crawl and re-index.</strong> Assistants with
            live retrieval hit the changed page first. Nothing visible
            changes in answers yet; you are waiting on the crawl.
          </li>
          <li>
            <strong>Days 3-7: technical changes surface.</strong> Technical
            indexing changes often reflect in AI responses within 3-7 days
            after schema deployment, structured data, FAQ and product
            markup, canonical and robots fixes are the fastest-moving
            levers because they change how the page is parsed, not how
            authoritative it is judged to be.
          </li>
          <li>
            <strong>Weeks 2-6: early visibility indicators.</strong> The
            first durable movement shows up as mention rate and citation
            rate on discovery and evaluation prompts. Expect direction, not
            a finished number: a stage moving from 0% to single digits is
            the signal to keep going.
          </li>
          <li>
            <strong>Weeks 6-12: decision-stage and share-of-voice shift.</strong>{" "}
            Comparison, alternatives, and vendor-selection prompts are last
            to move because they depend on third-party sources and reviewer
            evidence being recrawled, not just your own domain.
          </li>
        </ul>
        <p className="mt-3 text-sm text-muted-foreground">
          Timings reflect Monroya's own re-scan data on customer sites after
          a tracked fix ships. Sites with slower crawl budgets or blocked AI
          user agents sit at the long end of every band.
        </p>
      </Section>

      <Section heading="How the matrix is built">
        <p>
          We generate a draft prompt set from your category and ICP,
          organized by stage and persona. You review and edit. Prompts are
          tagged automatically, then scanned daily across every major AI
          assistant so each cell is auditable back to the raw responses.
          Each cell carries its own share of voice, its own top
          competitor, and its own opportunity queue.
        </p>
      </Section>

      <Section heading="What you do with it">
        <p>
          Three patterns recur. If you're weak in discovery, invest in
          category-defining content on third-party sources where AI answers
          pull from. If you're weak in evaluation, ship comparison and
          alternative pages on your own domain. If you're weak in decision,
          publish structured fact sheets, reviewer evidence, and direct
          competitor pages with current pricing.
        </p>
      </Section>

      <Section heading="Why we own this category">
        <p>
          Most AI visibility tools report one number. We separated the
          journey because operators kept telling us the average was
          useless, they needed to know which specific stage was bleeding
          pipeline. The matrix is the first thing every Monroya customer
          opens.
        </p>
      </Section>

    
      <Section heading="Related reading">
        <ul className="list-disc space-y-2 pl-6">
          <li>
            <Link
              to="/blog/$slug"
              params={{ slug: "evaluation-stage-first-what-19-tracked-companies-show-about-ai-visibility" }}
              className="font-medium text-ink underline decoration-border underline-offset-4 hover:decoration-ink"
            >
              Evaluation stage first: what 19 tracked companies show
            </Link>
          </li>
          <li>
            <Link
              to="/blog/$slug"
              params={{ slug: "how-do-i-know-if-ai-is-sending-customers-to-my-competitors" }}
              className="font-medium text-ink underline decoration-border underline-offset-4 hover:decoration-ink"
            >
              How do I know if AI is sending customers to my competitors?
            </Link>
          </li>
          <li>
            <Link
              to="/blog/$slug"
              params={{ slug: "how-long-until-a-new-company-shows-up-in-search" }}
              className="font-medium text-ink underline decoration-border underline-offset-4 hover:decoration-ink"
            >
              How long until a new company shows up in search?
            </Link>
          </li>
        </ul>
      </Section>
    </MarketingPage>
  );
}
